import { LitElement, html, nothing } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../shared/services/debug.service.js';
import { style } from './step-name.style.js';
import {
  MatchingStateEnum,
  offerStateEnum,
} from '../../model/customer-offer.model.js';
import { ApplicationService } from '../../../services/application.service.js';
import './eligibility-link/eligibility-link.component.js';
import './name/name.component.js';
import '../../key-requirements/key-requirements.component.js';
import './toggle-coborrower/toggle-coborrower.component.js';
import { globalStyle } from '../../../global-styles.style.js';
import { validateForm } from '../../../shared/decorators/validate-form.decorator.js';
import { stratum } from '../../../../constants/stratum.js';

class RefiStepNameComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepName');
    this.loading = false;
    this.customerOffer = {};
    this.validity = {};
    this.childIds = {
      primaryName: 'primary-name-input',
      coappName: 'coapp-name-input',
    };
    this.matchingState = ApplicationService.MatchingState;
  }

  static properties = {
    loading: { type: Boolean, reflect: true },
    customerOffer: { type: Object, attribute: 'customer-offer' },
    isJointApplication: { type: Boolean, reflect: true },
    application: { type: Object, reflect: true },
    applicant: { type: Object, reflect: true },
  };

  static styles = [style, globalStyle];

  connectedCallback() {
    super.connectedCallback();
    this.isJointApplication =
      ApplicationService.Application?.applicationType === 'JOINT';
    stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_NAME);
  }

  render() {
    return html`
      <form
        autocomplete="off"
        novalidate
        class="step-name-form"
        @submit="${this.onSubmit}"
        id="step-name-form"
      >
        <refi-heading
          class="modular-prequal__header"
          ?hidden="${this.customerOffer &&
          this.customerOffer.offerState === offerStateEnum.matched &&
          this.matchingState === MatchingStateEnum.primarySso}"
        >
          Let’s see if you’re pre-qualified!
          <refi-key-requirements slot="sub-header"></refi-key-requirements>
        </refi-heading>

        <fs-alert
          id="prefillAlert"
          type="informational"
          ?hidden="${!ApplicationService.Application.isPrefilled}"
          action-button="Okay"
          @request-action="${() =>
            this.shadowRoot.getElementById('prefillAlert').close()}"
        >
          We pre-filled with information we have on file. Please make sure it’s
          correct.
        </fs-alert>

        <refi-personal-name
          id="${this.childIds.primaryName}"
          @validity="${this.updateValidity}"
          applicant-type="primaryApplicant"
        ></refi-personal-name>
        <refi-toggle-coborrower
          @toggle-coborrower="${() => {
            this.isJointApplication = !this.isJointApplication;
          }}"
        ></refi-toggle-coborrower>

        ${this.isJointApplication
          ? html`<refi-personal-name
              id="${this.childIds.coappName}"
              @validity="${this.updateValidity}"
              applicant-type="coApplicant"
            >
              <span slot="firstName">First name of co-applicant</span>
              <span slot="lastName">Last name of co-applicant</span>
            </refi-personal-name>`
          : nothing}

        <div class="button-container">
          <fs-button class="fs-button next-button" variant="primary">
            <span>${this.isPrefilled ? 'Next' : 'Confirm'}</span>
          </fs-button>
        </div>
      </form>
    `;
  }

  updateValidity(ev) {
    this.validity[ev.detail.name] = ev.detail.isValid;
  }

  @validateForm()
  async onSubmit() {
    let valid = true;
    this.debug.log(this.validity);
    if (!this.isJointApplication) {
      Object.keys(this.validity)
        .filter((key) => !key.startsWith('co-'))
        .forEach((key) => {
          if (!this.validity[key]) {
            valid = false;
          }
        });
    } else {
      Object.values(this.validity).forEach((validityValue) => {
        if (!validityValue) {
          valid = false;
        }
      });
    }
    this.inputChildren = {
      primaryName: this.shadowRoot.getElementById(this.childIds.primaryName),
      coappName: this.shadowRoot.getElementById(this.childIds.coappName),
    };

    if (valid) {
      this.oldApplicantInfo = structuredClone(
        ApplicationService.Application.primaryApplicant
      );
      this.oldCoapplicantInfo = structuredClone(
        ApplicationService.Application.coApplicant
      );

      // Collect all updates
      const primaryApplicantUpdates = {};
      const coApplicantUpdates = {};
      Object.values(this.inputChildren).forEach((child) => {
        if (child) {
          Object.keys(child.values).forEach((field) => {
            if (this.isJointApplication && field.substring(0, 3) === 'co-') {
              coApplicantUpdates[field.substring(3)] = child.values[field];
            } else {
              primaryApplicantUpdates[field] = child.values[field];
            }
          });
        }
      });

      if (!this.isJointApplication) {
        coApplicantUpdates.firstName = '';
        coApplicantUpdates.lastName = '';
      }

      ApplicationService.Application = {
        ...ApplicationService.Application,
        applicationType: this.isJointApplication ? 'JOINT' : 'INDIVIDUAL',
        primaryApplicant: {
          ...ApplicationService.Application.primaryApplicant,
          ...primaryApplicantUpdates,
        },
        coApplicant: {
          ...ApplicationService.Application.coApplicant,
          ...coApplicantUpdates,
        },
      };

      this.tagFieldChangesOnSubmit();
      this.debug.log('valid', ApplicationService.Application);

      this.dispatchEvent(new CustomEvent('next-step'));
    }
    if (ApplicationService.Application.applicationType === 'JOINT') {
      this.dispatchEvent(
        new CustomEvent('co-app-added', {
          bubbles: true,
          composed: true,
        })
      );
      ApplicationService.updateExperiments(['iconic-ineligible'], [], 'iconic');
    }
  }

  tagFieldChangesOnSubmit() {
    Object.keys(ApplicationService.Application.primaryApplicant).forEach(
      (field) => {
        if (
          typeof ApplicationService.Application.primaryApplicant[field] !==
            'object' &&
          ApplicationService.Application.primaryApplicant[field] !==
            this.oldApplicantInfo[field]
        ) {
          this.debug.log(`${field} field changed`);
          stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
            replacements: {
              value: field,
            },
          });
        }
      }
    );
    Object.keys(ApplicationService.Application.coApplicant).forEach((field) => {
      if (
        typeof ApplicationService.Application.coApplicant[field] !== 'object' &&
        ApplicationService.Application.coApplicant[field] !==
          this.oldCoapplicantInfo[field]
      ) {
        this.debug.log(`co-${field} field changed`);
        stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
          replacements: {
            value: `co-${field}`,
          },
        });
      }
    });
  }
}

customElements.define('refi-step-name', RefiStepNameComponent);
